import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import DataLakesTable from "../DataLakes/DataLakesTable"; // Using the DataLakes table component
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Interface for Data Lakes
interface DataLake {
    id: string;
    lakeName: string; // The name of the Dataplex Lake
    projectId: string;
    location: string;
    status: string;
    solutionCategoryType: string; // Should be 'dataplexLake'
    cloudProvider: string;
    updatedAt: string;
    createdAt: string;
    solutionOwner?: SolutionOwnerInfo | null;
    reasonForRejection?: string;
    solutionType?: string;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof DataLake | string;
    groupBy: string;
}

let searchOptionsMasterList: SearchOptions[] = [];

const DataLakes: React.FC = () => {
    const [allDataLakes, setAllDataLakes] = useState<DataLake[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);

    // Updated column filters for Data Lakes
    const initialColumnFilters: Record<string, boolean> = {
        lakeName: true, // Changed from dataSourceName
        projectId: true,
        cloudProvider: true,
        location: true, // Added location
        solutionOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Using the API for Dataplex Lakes
                const response = await queryGraphql(`
                    query GetDataplexLakes {
                      getDataplexLakes { # Changed query name
                        id
                        name
                        solutionType
                        solutionCategoryType
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          dataplexLake { # Changed from dataSources
                            projectId
                            location
                            description
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        cloudProvider
                      }
                    }
                `);

                const dataLakesData = response.data?.getDataplexLakes || response.getDataplexLakes || []; // Changed response key

                if (dataLakesData) {
                    const processedDataLakes = dataLakesData.map((solution: any): DataLake => ({
                        id: solution.id,
                        projectId: solution.details?.dataplexLake?.projectId || "N/A",
                        lakeName: solution.name || "Unknown Data Lake", // Changed from dataSourceName
                        location: solution.details?.dataplexLake?.location || "N/A",
                        status: (solution.status || 'pending').toLowerCase(),
                        solutionCategoryType: solution.solutionCategoryType || "N/A", // Should be 'dataplexLake'
                        cloudProvider: solution.cloudProvider || "N/A",
                        solutionOwner: solution.solutionOwner,
                        solutionType: solution.solutionType,
                        updatedAt: solution.updatedAt,
                        createdAt: solution.createdAt,
                        reasonForRejection: solution.reasonForRejection,
                    }));
                    setAllDataLakes(processedDataLakes);

                    searchOptionsMasterList = processedDataLakes
                        .map((dl: DataLake) => [
                            { category: "lakeName", groupBy: "DATA LAKE NAME", chipLabel: `Lake: ${dl.lakeName}`, dropdownLabel: dl.lakeName },
                            { category: "projectId", groupBy: "PROJECT ID", chipLabel: `Project ID: ${dl.projectId}`, dropdownLabel: dl.projectId },
                            { category: "solutionCategoryType", groupBy: "CATEGORY TYPE", chipLabel: `Category: ${dl.solutionCategoryType}`, dropdownLabel: dl.solutionCategoryType },
                            { category: "cloudProvider", groupBy: "CLOUD PROVIDER", chipLabel: `Provider: ${dl.cloudProvider}`, dropdownLabel: dl.cloudProvider },
                            { category: "location", groupBy: "LOCATION", chipLabel: `Location: ${dl.location}`, dropdownLabel: dl.location },
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${dl.status}`, dropdownLabel: dl.status.charAt(0).toUpperCase() + dl.status.slice(1) },
                            ...(dl.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "RESOURCE OWNER", chipLabel: `Owner: ${dl.solutionOwner.name}`, dropdownLabel: dl.solutionOwner.name }] : []),
                            ...(dl.solutionType ? [{ category: "solutionType", groupBy: "SOLUTION TYPE", chipLabel: `Type: ${dl.solutionType}`, dropdownLabel: dl.solutionType }] : []),
                        ])
                        .flat();

                    searchOptionsMasterList = searchOptionsMasterList.filter(
                        (obj, index, self) =>
                            obj.dropdownLabel &&
                            index === self.findIndex((t) =>
                                t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                            )
                    );
                    searchOptionsMasterList.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = a.dropdownLabel.toUpperCase();
                        const labelB = b.dropdownLabel.toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                }
            } catch (error) {
                console.error("Error fetching Data Lakes:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        if (key === 'solutionOwner') return 'Resource Owner';
        if (key === 'createdAt') return 'Added On';
        if (key === 'lakeName') return 'Data Lake Name'; // Changed
        if (key === 'solutionCategoryType') return 'Category Type';
        if (key === 'cloudProvider') return 'Cloud Provider';
        if (key === 'location') return 'Location'; // Added
        const result = key.replace(/([A-Z])/g, ' $1');
        return result.charAt(0).toUpperCase() + result.slice(1).trim();
    };


    const globallyFilteredDataLakes =
        searchQuery.length === 0
            ? allDataLakes
            : allDataLakes.filter((dataLake: DataLake) => {
                return searchQuery.every(queryParam => {
                    let dataLakeValue: any;
                    if (queryParam.category === "solutionOwner.name") {
                        dataLakeValue = dataLake.solutionOwner?.name;
                    } else {
                        dataLakeValue = dataLake[queryParam.category as keyof DataLake];
                    }
                    if (dataLakeValue === null || dataLakeValue === undefined) return false;
                    return String(dataLakeValue).toLowerCase().includes(queryParam.dropdownLabel.toLowerCase());
                });
            });

    const pendingDataLakes = globallyFilteredDataLakes.filter(p => p.status === 'pending');
    const activeDataLakes = globallyFilteredDataLakes.filter(p => p.status === 'active' || p.status === 'approved');
    const rejectedDataLakes = globallyFilteredDataLakes.filter(p => p.status === 'rejected');

    const totalDataLakesOverall = allDataLakes.length;
    const totalMatchingSearch = globallyFilteredDataLakes.length;

    const canAddDataLake = true; // Assuming same logic as DataSources

    const handleAddDataLake = () => {
        navigate("/datasolutions/data-lakes/add"); // Changed navigation path
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"datasolutions-data-lakes"} /> {/* Changed index */}
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"datasolutions-data-lakes"} /> {/* Changed index */}
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                mx:2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                Data Lakes {/* Changed text */}
                            </Typography>
                            <Box sx={{display:'flex', justifyContent:'center'}}>
                                <Box>
                            {canAddDataLake && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "black", color: "white", padding: "8px 24px",
                                        fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                         '&:hover': { backgroundColor: '#333333' }
                                    }}
                                    onClick={handleAddDataLake}
                                    disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                >
                                    Add New Data Lake {/* Changed text */}
                                </Button>
                                
                            )}
                            </Box>
                            <IconButton
                                                                  aria-label="Help" 
                                                                  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                                                  sx={{
                                                                    padding: 0, 
                                                                    border: 'none',  
                                                                    ml:2,
                                                                    '&:hover': {
                                                                      
                                                                      backgroundColor: 'action.hover', 
                                                                    },
                                                                    
                                                                  }}
                                                                >
                                                                  <Box
                                                                    component="img"
                                                                    src="/assets/Help.svg" 
                                                                    alt="" 
                                                                    sx={{
                                                                      width: 40,  
                                                                      height: 40,
                                                                      display: 'block', 
                                                                    }}
                                                                  />
                                                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by lake name, project, status, etc." 
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                sx={{
                                
                                    border: "1px solid #bbb",
                                    borderRadius: "10px",
                                    height: "45px",
                                    width: "45px",
                                }}
                                onClick={handleColumnFilterClick}
                            >
                                <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                            </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{xs: 1, sm:2}}>
                        {totalDataLakesOverall > 0 && totalMatchingSearch > 0 &&(
                            <>
                        <Accordion
                            defaultExpanded={pendingDataLakes.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={pendingDataLakes.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Data Lakes ({pendingDataLakes.length}) {/* Changed text */}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {pendingDataLakes.length > 0 ? (
                                    <DataLakesTable // Changed component
                                        dataLakes={pendingDataLakes}
                                        columnFilters={columnFilters}
                                        statusType="pending"
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No pending Data Lakes match your filters. {/* Changed text */}
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={activeDataLakes.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={activeDataLakes.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Data Lakes ({activeDataLakes.length}) {/* Changed text */}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {activeDataLakes.length > 0 ? (
                                    <DataLakesTable // Changed component
                                        dataLakes={activeDataLakes}
                                        columnFilters={columnFilters}
                                        statusType="active"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No active Data Lakes match your filters. {/* Changed text */}
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={rejectedDataLakes.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={rejectedDataLakes.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Data Lakes ({rejectedDataLakes.length}) {/* Changed text */}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {rejectedDataLakes.length > 0 ? (
                                    <DataLakesTable // Changed component
                                        dataLakes={rejectedDataLakes}
                                        columnFilters={columnFilters}
                                        statusType="rejected"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No rejected Data Lakes match your filters. {/* Changed text */}
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        </>)}
                         {totalDataLakesOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor:"#FFFFFF", borderRadius:"8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No Data Lakes Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Data Lakes match your search {/* Changed text */}
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {totalDataLakesOverall === 0 && !isLoading && (
                             <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No Data Lakes" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Data Lakes are added yet {/* Changed text */}
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first Data Lake. {/* Changed text */}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataLakes;